/**
 * Bound Tauri geolocation IPC that can hang forever on some SlimBOX boots
 * (`check_permissions` / `watch_position` never resolve → overlay stuck on "desativado").
 * Do **not** wrap `requestPermissions` — that waits on the OS dialog.
 */
export const NATIVE_GEO_IPC_TIMEOUT_MS = 15_000

/** Reject if a native geo promise never settles (plugin IPC hang). */
export function withNativeGeoIpcTimeout<T>(
  promise: Promise<T>,
  label: string,
  timeoutMs: number = NATIVE_GEO_IPC_TIMEOUT_MS
): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error(`Geolocation IPC timeout after ${timeoutMs}ms (${label})`))
    }, timeoutMs)
    promise.then(
      (value) => {
        clearTimeout(timer)
        resolve(value)
      },
      (err: unknown) => {
        clearTimeout(timer)
        reject(err)
      }
    )
  })
}

/**
 * After abandoning a timed-out `watchPosition` await, clear the channel if the
 * native invoke still resolves later — otherwise the listener stays registered
 * while `nativeWatchId` was never stored (`stop()` cannot tear it down).
 */
export function scheduleClearOrphanNativeGeoWatch(params: {
  watchPromise: Promise<number>
  clearWatch: (channelId: number) => void | Promise<void>
  /** True when this channel is the service's active watch (do not clear). */
  isOwnedChannel: (channelId: number) => boolean
}): void {
  void params.watchPromise
    .then(async (channelId) => {
      if (params.isOwnedChannel(channelId)) {
        return
      }
      await params.clearWatch(channelId)
    })
    .catch(() => {
      // best-effort; process exit clears watches anyway
    })
}
